import React from "react";
import { Link } from "react-router-dom";
import { Users, Briefcase, Car, ArrowRight, Phone, Zap, VolumeX, Leaf } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import SEO from "../components/SEO";

const HERO_IMG = "https://customer-assets-4nw71qhi.emergentagent.net/job_brothers-taxi-web/artifacts/96596r9a_brothers%20taxi%20tirana%20airport.png";
const ELECTRIC_SECTION_IMG = "https://customer-assets-4nw71qhi.emergentagent.net/job_brothers-taxi-web/artifacts/q718vh2m_byd%20sarande%20brotherstaxi.png";

const FLEET_IMAGES = {
  id4: "https://customer-assets-4nw71qhi.emergentagent.net/job_brothers-taxi-web/artifacts/qljglrgo_brothers%20taxi%20id4%20tirana%20airport.png",
  mg4: "https://customer-assets-4nw71qhi.emergentagent.net/job_brothers-taxi-web/artifacts/5t1gwi58_mg4%20brotherstaxi.png",
  byd: "https://customer-assets-4nw71qhi.emergentagent.net/job_brothers-taxi-web/artifacts/q718vh2m_byd%20sarande%20brotherstaxi.png",
};

const ELECTRIC_ICONS = [Zap, VolumeX, Leaf];

const Fleet = () => {
  const { t } = useLang();

  return (
    <>
      <SEO title={t.seo.fleet.title} description={t.seo.fleet.description} keywords={t.seo.fleet.keywords} path="/fleet" />

      {/* ============ HERO – text left, full-bleed image right ============ */}
      <section className="relative pt-32 md:pt-36 pb-16 md:pb-24 bg-cream overflow-hidden">
        <div className="absolute -top-10 -left-10 w-72 h-72 rounded-full bg-[#FFD500] blur-[140px] opacity-25 pointer-events-none" />
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-0 items-center">
          {/* Left: title */}
          <div className="lg:col-span-5 xl:col-span-5 anim-fade-up pl-6 md:pl-12 pr-6 md:pr-12 lg:pr-0">
            <h1 className="font-display text-3xl md:text-4xl lg:text-[44px] font-bold tracking-tight text-zinc-900 leading-[1.05]">
              {t.fleet.title}
            </h1>
            <p className="mt-5 text-zinc-600 text-base md:text-lg max-w-xl leading-relaxed">{t.fleet.subtitle}</p>

            <div className="mt-7 flex flex-wrap gap-3">
              <a href="#our-vehicles" className="bt-btn-primary" data-testid="fleet-hero-cta-view">
                {t.fleet.ctaVehicles}
                <ArrowRight className="h-4 w-4" />
              </a>
              <Link to="/book" className="bt-btn-outline" data-testid="fleet-hero-cta-book">
                {t.common.bookTaxi}
              </Link>
            </div>
          </div>

          {/* Right: full-bleed image */}
          <div className="lg:col-span-7 xl:col-span-7 anim-fade-up anim-delay-2 relative">
            <div className="relative overflow-hidden lg:rounded-l-[48px] xl:rounded-l-[60px] shadow-taxi-lift">
              <img
                src={HERO_IMG}
                alt="Brothers Taxi - flotë BYD elektrike në Aeroportin Ndërkombëtar të Tiranës (Rinas)"
                className="w-full h-[340px] sm:h-[440px] md:h-[540px] lg:h-[600px] xl:h-[640px] object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/60 via-black/10 to-transparent pointer-events-none" />
              <div className="absolute bottom-4 left-4 md:bottom-6 md:left-6">
                <div className="inline-flex items-center gap-2 bg-white/95 backdrop-blur px-4 py-2 rounded-full text-[10px] md:text-[11px] uppercase tracking-widest font-extrabold text-zinc-900">
                  <Leaf className="h-3.5 w-3.5 text-zinc-900" strokeWidth={2.5} />
                  Aeroporti Tiranë · BYD Song Plus 2025
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ ELECTRIC FLEET FEATURE SECTION ============ */}
      <section className="bg-mist py-20 md:py-28 relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-80 h-80 rounded-full bg-[#FFD500] blur-[130px] opacity-25 pointer-events-none" />
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Left: photo */}
          <div className="lg:col-span-6 anim-fade-up">
            <div className="relative">
              <div className="absolute -bottom-4 -left-4 h-24 w-24 checkered-fine opacity-40 pointer-events-none" />
              <div className="relative overflow-hidden rounded-[28px] md:rounded-[36px] border border-zinc-200 shadow-taxi-lift">
                <img
                  src={ELECTRIC_SECTION_IMG}
                  alt="BYD Song Plus elektrik i Brothers Taxi në bregdetin e Sarandës"
                  className="w-full h-[340px] md:h-[520px] object-cover"
                />
                <div className="absolute top-5 left-5">
                  <div className="inline-flex items-center gap-1.5 bg-[#FFD500] text-zinc-900 px-3 py-1.5 rounded-full text-[10px] md:text-[11px] uppercase tracking-widest font-extrabold">
                    <Zap className="h-3 w-3" strokeWidth={3} />
                    100% Electric · 2025
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: text */}
          <div className="lg:col-span-6 anim-fade-up anim-delay-2">
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-zinc-900 leading-[1.05]">
              {t.fleet.electric.title}
            </h2>
            <p className="mt-5 text-zinc-600 text-base md:text-lg leading-relaxed">{t.fleet.electric.subtitle}</p>

            <ol className="mt-10 space-y-6">
              {t.fleet.electric.items.map((item, i) => {
                const Icon = ELECTRIC_ICONS[i] || Zap;
                return (
                  <li
                    key={i}
                    data-testid={`electric-item-${i}`}
                    className="flex items-start gap-5"
                  >
                    <div className="flex-shrink-0 flex flex-col items-center">
                      <div className="font-display text-xl md:text-2xl font-bold text-zinc-900 tracking-tight leading-none">
                        {String(i + 1).padStart(2, "0")}
                      </div>
                      <div className="mt-2 h-9 w-9 rounded-full bg-[#FFD500] flex items-center justify-center">
                        <Icon className="h-4 w-4 text-zinc-900" strokeWidth={2.5} />
                      </div>
                    </div>
                    <div className="pt-1">
                      <h3 className="font-display text-base md:text-lg font-bold text-zinc-900 tracking-tight">
                        {item.title}
                      </h3>
                      <p className="mt-1.5 text-zinc-600 leading-relaxed">{item.desc}</p>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ 3 VEHICLE CARDS (alternating) ============ */}
      <section id="our-vehicles" className="bg-paper py-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 space-y-8">
          {t.fleet.vehicles.map((v, i) => (
            <div
              key={i}
              data-testid={`fleet-card-${v.type}`}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-3xl overflow-hidden border border-zinc-200 shadow-taxi-card hover:shadow-taxi-lift transition-all group ${
                i % 2 === 1 ? "lg:[&>div:first-child]:order-2" : ""
              }`}
            >
              <div className="relative h-[300px] lg:h-[460px] bg-zinc-100 overflow-hidden">
                <img
                  src={FLEET_IMAGES[v.type]}
                  alt={`Brothers Taxi ${v.name}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute top-5 left-5 bt-tag-yellow uppercase flex items-center gap-1.5">
                  <Zap className="h-3 w-3" strokeWidth={3} />
                  2025
                </div>
              </div>
              <div className={`p-8 lg:p-12 flex flex-col justify-center ${i === 1 ? "bg-zinc-900 text-white" : "bg-white"}`}>
                <h2 className={`font-display text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight mb-4 leading-[1.05] ${i === 1 ? "text-white" : "text-zinc-900"}`}>
                  {v.name}
                </h2>
                <p className={`text-base md:text-lg mb-8 leading-relaxed ${i === 1 ? "text-zinc-300" : "text-zinc-700"}`}>{v.desc}</p>

                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className={`rounded-2xl p-4 ${i === 1 ? "bg-zinc-800" : "bg-cream"}`}>
                    <Users className={`h-5 w-5 mb-2 ${i === 1 ? "text-[#FFD500]" : "text-zinc-900"}`} />
                    <div className={`text-xs uppercase tracking-widest font-bold ${i === 1 ? "text-zinc-400" : "text-zinc-500"}`}>{t.common.capacity}</div>
                    <div className={`font-display font-bold ${i === 1 ? "text-white" : "text-zinc-900"}`}>{v.capacity}</div>
                  </div>
                  <div className={`rounded-2xl p-4 ${i === 1 ? "bg-zinc-800" : "bg-cream"}`}>
                    <Briefcase className={`h-5 w-5 mb-2 ${i === 1 ? "text-[#FFD500]" : "text-zinc-900"}`} />
                    <div className={`text-xs uppercase tracking-widest font-bold ${i === 1 ? "text-zinc-400" : "text-zinc-500"}`}>{t.common.luggage}</div>
                    <div className={`font-display font-bold ${i === 1 ? "text-white" : "text-zinc-900"}`}>{v.luggage}</div>
                  </div>
                </div>

                <Link to="/book" className="bt-btn-primary w-fit">
                  <Car className="h-4 w-4" /> {t.common.bookTaxi}
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20 md:py-24 bg-[#FFD500] relative overflow-hidden">
        <div className="absolute -right-10 -top-10 w-72 h-72 rounded-full bg-zinc-900 opacity-5 blur-3xl" />
        <div className="absolute -left-10 -bottom-10 w-72 h-72 rounded-full bg-zinc-900 opacity-5 blur-3xl" />
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center relative">
          <h3 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-zinc-900 mb-5 tracking-tight leading-[1.05]">
            {t.fleet.ctaTitle}
          </h3>
          <p className="text-zinc-800 text-base md:text-lg max-w-2xl mx-auto mb-8 font-medium leading-relaxed">
            {t.fleet.ctaDesc}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/book" className="bt-btn-secondary" data-testid="fleet-cta-book">{t.common.bookTaxi} <ArrowRight className="h-4 w-4" /></Link>
            <a href="tel:+355682222286" className="inline-flex items-center justify-center gap-2 bg-white text-zinc-900 border-2 border-zinc-900 font-extrabold uppercase tracking-wider hover:bg-zinc-900 hover:text-white transition-colors rounded-full px-7 py-4 text-sm" data-testid="fleet-cta-call">
              <Phone className="h-4 w-4" /> +355 68 22 22 286
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default Fleet;
